import React, { useState, useEffect } from 'react';
import { Icon, Popup } from 'semantic-ui-react';
import apiClient from '../apiTools/apiClient';

/**
 * A small navbar indicator showing the signed-in user's standing on the login allowlist.
 *
 * ⭐ THE STATE WORTH SHOWING IS THE WARNING ONE, and it only appears in development.
 *
 * In production, anyone signed in is necessarily permitted — enforcement made sure of it — so a
 * green tick there confirms what being logged in already proves. In development nothing is
 * enforced, which means this is the only way to learn whether an account WOULD be admitted once it
 * matters. That is the question worth answering before inviting a voice actor: not "am I in", but
 * "will this work when it counts".
 *
 * Four states, and they are deliberately not equally loud:
 *
 *   enforcing + allowlist    quiet green check     — confirms the ordinary path
 *   enforcing + break-glass  orange key            — the list did not admit you; the env var did
 *   dev + would be admitted  grey check            — reassurance, deliberately dim
 *   dev + would be DENIED    blue triangle         — the one that earns the space
 *
 * ⭐ ONLY THE BREAK-GLASS KEY IS COLOURED AS A CAUTION, because it is the only state describing
 * something true right now that wants changing: you got in around the list rather than through it.
 * The denied state reads loud in shape but calm in colour — a triangle in blue — because it is a
 * forecast about production, not a fault in the app you are looking at.
 */
const AllowlistBadge = () => {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        /**
         * ⛔ DO NOT ASK WITHOUT A TOKEN (2026-10-26). The navbar renders this only when
         * useIsLoggedIn() returns one, but a render and the axios interceptor's read of storage are
         * not the same instant — and a sign-out lands squarely between them. The result was a 401
         * on a bare request, recorded as a breadcrumb, which then became the login page's
         * explanation for a sign-out the operator had chosen: "you were not signed in on this
         * device". The question this badge asks is about the signed-in account; with no token there
         * is no account to ask about, and the request can only produce a misleading crumb.
         */
        if (!localStorage.getItem('authToken')) return;
        const { data } = await apiClient.get('/login-allowlist/me');
        if (!cancelled) setStatus(data);
      } catch (_) {
        // Silent. An indicator that cannot fetch its own state must not become an error message in
        // the navbar — the page is not about this.
        if (!cancelled) setStatus(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!status) return null;

  const { enforcing, isBreakGlass, entryDisabled, wouldBeAdmitted } = status;

  let icon = 'check circle outline';
  let color = 'grey';
  let heading = '';
  let detail = '';

  if (enforcing && isBreakGlass) {
    icon = 'key'; color = 'orange';
    heading = 'Signed in with the break-glass account';
    detail = 'Permitted by server configuration rather than by the allowlist. If this is how you '
      + 'normally get in, the allowlist needs an entry for you.';
  } else if (enforcing) {
    icon = 'check circle'; color = 'green';
    heading = 'On the login allowlist';
    detail = 'This environment only admits addresses on the allowlist, and yours is one of them.';
  } else if (wouldBeAdmitted) {
    icon = 'check circle outline'; color = 'grey';
    heading = 'On the allowlist — not enforced here';
    detail = isBreakGlass
      ? 'You are the break-glass account, so you would be admitted in production.'
      : 'Any account can sign in to this environment. In production, yours would be admitted.';
  } else {
    // ⭐ The state this component exists for — and deliberately NOT coloured as a fault.
    //
    // It only ever appears in development, where nothing is enforced, so nothing is broken when it
    // shows. It answers "would this account work in production", and the answer being "no" is
    // information to act on later, not an error happening now. Amber read as something being wrong
    // with the running app. Blue says look at this without saying something has failed.
    icon = 'exclamation triangle'; color = 'blue';
    heading = entryDisabled
      ? 'Your allowlist entry is DISABLED'
      : 'Not on the login allowlist';
    detail = 'You can sign in here because this environment does not enforce the allowlist. '
      + 'In production this account would be refused. '
      + (entryDisabled
        ? 'An admin disabled the entry — re-enable it under System Management → Login Allowlist.'
        : 'An admin can admit it under System Management → Login Allowlist.');
  }

  return (
    <Popup
      on={['hover', 'focus']}
      position="bottom right"
      // [skidding, distance]. For a `bottom` base placement Popper maps skidding straight to x,
      // so a positive value nudges the panel right — clear of the navbar's right edge. 8px is
      // measured against the rendered navbar, not chosen.
      offset={[8, 0]}
      trigger={
        <span
          tabIndex={0}
          aria-label={heading}
          style={{ display: 'inline-flex', alignItems: 'center', cursor: 'default' }}
        >
          <Icon name={icon} color={color} />
        </span>
      }
    >
      <Popup.Header>{heading}</Popup.Header>
      <Popup.Content style={{ maxWidth: '22em' }}>{detail}</Popup.Content>
    </Popup>
  );
};

export default AllowlistBadge;
